import { useReducer, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { format, subSeconds } from 'date-fns';
import { setState } from '@chronos/ui-toolkit/actions';
import * as timeUtil from '@chronos/time-util';
import keyValueReducer from '@chronos/ui-toolkit/reducers/keyValueReducer';

import Checkbox from '../Checkbox';
import TrackedProgress from '../TrackedProgress';
import DateTimePicker from '../DateTimePicker';
import * as S from './styled';
import FormSelect from '../FormSelect';

const updateTime = (datetime, { h, m, s }) => {
  const newDateTime = new Date(datetime.getTime());
  newDateTime.setHours(parseInt(h || '0', 10));
  newDateTime.setMinutes(parseInt(m || '0', 10));
  newDateTime.setSeconds(parseInt(s || '0', 10));
  return newDateTime;
};

const getDateTimeState = (startTime, timeSpentInSeconds = 0) => {
  const adjustedStartTime = !Number.isNaN(timeSpentInSeconds)
    ? subSeconds(startTime, timeSpentInSeconds)
    : startTime;

  return {
    startDateTime: startTime,
    adjustedStartTime,
  };
};

const initialState = {
  timeEstimate: null,
  progressStats: {
    leftPercentage: 100,
    rightPercentage: 0,
    isOverEstimate: false,
    leftMark: '0m logged',
    rightMark: '',
  },
  timeStr: '',
};

function calculateEstimate({ estimates, totalTime, timeSpent }) {
  const timeEstimate = estimates.reduce(
    (acc, estimate) => acc + estimate.seconds,
    0
  );
  return {
    timeEstimate,
    progressStats: timeUtil.calculateTimeProgress({
      trackedTime: totalTime + timeUtil.jts(timeSpent),
      estimate: timeEstimate,
    }),
  };
}

const WorklogForm = (props) => {
  const {
    mode,
    skin,
    estimates,
    isCommentRequired,
    totalTime,
    setFormData,
    formData,
    handleSubmit,
    worklogTime,
    weekStartsOn,
    cardsSelectProps,
    usersSelectProps,
  } = props;

  const [state, dispatch] = useReducer(keyValueReducer, {
    ...initialState,
    ...calculateEstimate({
      estimates,
      totalTime,
      timeSpent: formData.timeSpent,
    }),
  });

  useEffect(() => {
    const datetime = formData.adjustStartTime
      ? formData.adjustedStartTime
      : formData.startDateTime;

    dispatch(
      setState({
        timeStr: format(datetime, 'HH:mm'),
      })
    );
  }, [
    formData.adjustStartTime,
    formData.adjustedStartTime,
    formData.startDateTime,
  ]);

  useEffect(() => {
    dispatch(
      setState(
        calculateEstimate({
          estimates,
          totalTime,
          timeSpent: formData.timeSpent,
        })
      )
    );
  }, [estimates, totalTime, formData.timeSpent]);

  const handleTimeSpentChange = useCallback(
    (ev) => {
      const newTimeSpent = ev.target.value || '';
      const timeSpentInSeconds = timeUtil.jts(newTimeSpent);

      const adjustmentTime = formData.adjustStartTime
        ? timeSpentInSeconds - (worklogTime || 0)
        : 0;

      const startDateTimeState = getDateTimeState(
        formData.startDateTime,
        adjustmentTime
      );

      setFormData({
        ...startDateTimeState,
        timeSpent: newTimeSpent,
        errors: {
          ...formData.errors,
          timeSpent: '',
        },
      });

      dispatch(
        setState({
          progressStats: timeUtil.calculateTimeProgress({
            trackedTime: timeSpentInSeconds - (worklogTime || 0) + totalTime,
            estimate: state.timeEstimate,
          }),
        })
      );
    },
    [
      setFormData,
      worklogTime,
      totalTime,
      formData.adjustStartTime,
      formData.startDateTime,
      state.timeEstimate,
    ]
  );

  const handleDateStartedChange = useCallback(
    (startDateTime) => {
      const startDateTimeState = getDateTimeState(startDateTime);

      setFormData({
        ...startDateTimeState,
        adjustStartTime: false,
      });
    },
    [setFormData]
  );

  const handleTimeStartedChange = useCallback(
    (ev) => {
      const { value } = ev.target;
      dispatch(
        setState({
          timeStr: value,
        })
      );
      if (value.includes('_')) return;

      const [h, m] = value.split(':');
      const startDateTime = updateTime(formData.startDateTime, { h, m });
      const startDateTimeState = getDateTimeState(startDateTime);

      setFormData({
        ...startDateTimeState,
        adjustStartTime: false,
      });
    },
    [setFormData, formData.startDateTime]
  );

  const handleAdjustStartTimeChange = useCallback(() => {
    const adjustStartTime = !formData.adjustStartTime;

    let startDateTimeState = getDateTimeState(formData.startDateTime);

    if (adjustStartTime) {
      const timeSpentInSeconds = timeUtil.jts(formData.timeSpent);
      const adjustmentTime = timeSpentInSeconds - (worklogTime || 0);
      startDateTimeState = getDateTimeState(
        formData.startDateTime,
        adjustmentTime
      );
    }

    setFormData({
      ...startDateTimeState,
      adjustStartTime,
    });
  }, [
    setFormData,
    formData.adjustStartTime,
    formData.startDateTime,
    formData.timeSpent,
    worklogTime,
  ]);

  const handleChange = (attr) => (value) => {
    setFormData({ [attr]: value, errors: { ...formData.errors, [attr]: '' } });
  };

  const datetime = formData.adjustStartTime
    ? formData.adjustedStartTime
    : formData.startDateTime;

  return (
    <>
      <S.FieldWrapper skin={skin}>
        {skin === 'pwa' && (
          <S.InputLabel paddingTop="15">
            Card
            <S.RequiredIndicator>*</S.RequiredIndicator>
          </S.InputLabel>
        )}
        <FormSelect
          {...cardsSelectProps}
          onChange={handleChange('cardId')}
          value={formData.cardId}
          getValue={({ id }) => id}
          getLabel={({ name }) => name}
          isInvalid={formData.errors.cardId !== ''}
        />
      </S.FieldWrapper>
      <S.FieldWrapper>
        {state.timeEstimate ? (
          <S.EstimateText>
            <span>{'The estimate for this card is '}</span>
            <S.EstimateTime>{timeUtil.stj(state.timeEstimate)}</S.EstimateTime>
          </S.EstimateText>
        ) : (
          <S.EstimateText>No estimate for this card</S.EstimateText>
        )}

        <TrackedProgress
          showAvatarsIfEmpty={false}
          stats={state.progressStats}
          size="big"
        />
        <S.InputLabel paddingTop="15">
          Assign to
          <S.RequiredIndicator>*</S.RequiredIndicator>
        </S.InputLabel>
        <FormSelect
          {...usersSelectProps}
          onChange={handleChange('userId')}
          value={formData.userId}
          getValue={({ id }) => id.toString()}
          getLabel={({ fullName }) => fullName}
        />
      </S.FieldWrapper>

      <S.FieldWrapper>
        <S.InputLabel>
          Time spent
          <S.RequiredIndicator>*</S.RequiredIndicator>
        </S.InputLabel>
        <S.InputRow>
          <S.Input
            autoFocus
            required
            value={formData.timeSpent}
            onChange={handleTimeSpentChange}
            isInvalid={formData.errors.timeSpent !== ''}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && handleSubmit) {
                handleSubmit();
              }
            }}
          />
          <S.InputExample>(eg. 1h 30m)</S.InputExample>
        </S.InputRow>
        <S.Error>{formData.errors.timeSpent}</S.Error>
        {mode !== 'startReview' && (
          <Checkbox
            name="adjust_start_time"
            id="adjust_start_time"
            label="Adjust start time according to time spend"
            isChecked={formData.adjustStartTime ?? false}
            components={{
              CheckboxItem: S.CheckboxItem,
            }}
            onChange={handleAdjustStartTimeChange}
          />
        )}
      </S.FieldWrapper>
      {mode !== 'startReview' && (
        <S.FieldWrapper>
          <S.InputLabel>
            Date started
            <S.RequiredIndicator>*</S.RequiredIndicator>
          </S.InputLabel>
          <S.DateTimePickerWrapper>
            <DateTimePicker
              withTime={false}
              readOnly
              weekStartsOn={weekStartsOn}
              dateTime={datetime}
              onChange={handleDateStartedChange}
            />
            <S.TimeInput
              mask={[/[0-2]/, /[0-9]/, ':', /[0-5]/, /[0-9]/]}
              value={state.timeStr}
              onFocus={(ev) => {
                ev.target.select();
              }}
              onChange={handleTimeStartedChange}
            />
          </S.DateTimePickerWrapper>
        </S.FieldWrapper>
      )}
      <S.FieldWrapper>
        <S.WorklogComment>
          <S.InputLabel>
            Worklog comment
            {isCommentRequired && <S.RequiredIndicator>*</S.RequiredIndicator>}
          </S.InputLabel>
          <S.Textarea
            minRows={4}
            maxRows={10}
            value={formData.comment || ''}
            isInvalid={formData.errors.comment !== ''}
            onChange={(e) => {
              handleChange('comment')(e.target.value);
            }}
          />
        </S.WorklogComment>
      </S.FieldWrapper>
    </>
  );
};

WorklogForm.defaultProps = {
  worklogTime: 0,
  totalTime: 0,
  estimates: [],
  isCommentRequired: false,
  weekStartsOn: 0,
};

WorklogForm.propTypes = {
  worklogTime: PropTypes.number,
  handleSubmit: PropTypes.func,
  setFormData: PropTypes.func.isRequired,
  formData: PropTypes.shape({
    cardId: PropTypes.string,
    userId: PropTypes.string,
    adjustStartTime: PropTypes.bool,
    comment: PropTypes.string,
    timeSpent: PropTypes.string,
    startDateTime: PropTypes.instanceOf(Date),
    adjustedStartTime: PropTypes.instanceOf(Date),
    errors: PropTypes.shape({
      cardId: PropTypes.string,
      comment: PropTypes.string,
      timeSpent: PropTypes.string,
    }),
  }).isRequired,
  estimates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      seconds: PropTypes.number,
    })
  ),
  mode: PropTypes.oneOf([
    'addWorklog',
    'editWorklog',
    'startReview',
    'stopReview',
  ]).isRequired,
  skin: PropTypes.oneOf(['powerup', 'pwa']).isRequired,
  isCommentRequired: PropTypes.bool,
  totalTime: PropTypes.number,
  weekStartsOn: PropTypes.number,

  cardsSelectProps: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      })
    ),
    isLoading: PropTypes.bool,
    isDisabled: PropTypes.bool,
  }),

  usersSelectProps: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        fullName: PropTypes.string,
      })
    ),
    isLoading: PropTypes.bool,
    isDisabled: PropTypes.bool,
  }),
};

export default WorklogForm;
